import { defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import { granularityLevelSchema } from '../shared/schemes.js';

const MetricSchema = z.object({
  label: z.string(),
  name: z.string(),
  required: z.boolean(),
  headerType: z.enum(['metric', 'dimension']),
  // Mint 2.0 only: the metric is deactivated in MDM
  isDisabled: z.boolean().optional(),
});

export type SystemMetric = z.infer<typeof MetricSchema>;

const MetricsQuerySchema = z.object({
  granularityLevelType: granularityLevelSchema.nullable(),
});

const TemplateMetricsQuerySchema = z.object({
  granularityLevel: granularityLevelSchema,
});

const MetricsResponseSchema = z.object({
  data: z.object({
    items: z.array(MetricSchema),
    additional: z.object({
      customMetrics: z.array(MetricSchema).readonly(),
      maxCount: z.number().optional(),
    }),
  }),
});

export type MetricsResponse = z.infer<typeof MetricsResponseSchema>;
export type MetricsQueryResult = MetricsResponse['data'];

export const getSystemMetricsQueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/granularity/headers-meta/' as const,
  method: 'GET',
  querySchema: MetricsQuerySchema,
  responseSchema: MetricsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): MetricsQueryResult {
    return data.data;
  },
  keyPrefix: ['metricsList'],
});

export const getSimplifiedSystemMetricsQueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/granularity/headers-meta/v2/' as const,
  method: 'GET',
  querySchema: MetricsQuerySchema,
  responseSchema: MetricsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): MetricsQueryResult {
    return data.data;
  },
  keyPrefix: ['metricsList'],
});

export const getDataFileSystemMetricsQueryOptions = defaultClient.query({
  url: 'data-ingestion/tabledatafile/$id/headers-meta/' as const,
  method: 'GET',
  querySchema: MetricsQuerySchema,
  responseSchema: MetricsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): MetricsQueryResult {
    return data.data;
  },
  keyPrefix: ['metricsList'],
});

export const getSimplifiedDataFileSystemMetricsQueryOptions =
  defaultClient.query({
    url: 'data-ingestion/tabledatafile/$id/headers-meta/v2/' as const,
    method: 'GET',
    querySchema: MetricsQuerySchema,
    responseSchema: MetricsResponseSchema,
    snakeCaseQueryParams: true,
    processResponse(data): MetricsQueryResult {
      return data.data;
    },
    keyPrefix: ['metricsList'],
  });

export const getTemplateMetricsQueryOptions = defaultClient.query({
  url: 'data-ingestion/activity/$activityId/headers-meta/' as const,
  method: 'GET',
  querySchema: TemplateMetricsQuerySchema,
  responseSchema: MetricsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): MetricsQueryResult {
    return data.data;
  },
  keyPrefix: ['metricsList'],
});

export const getSimplifiedTemplateMetricsQueryOptions = defaultClient.query({
  url: 'data-ingestion/activity/$activityId/headers-meta/v2/' as const,
  method: 'GET',
  querySchema: TemplateMetricsQuerySchema,
  responseSchema: MetricsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): MetricsQueryResult {
    return data.data;
  },
  keyPrefix: ['metricsList'],
});
